import { Form, Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import Button from 'src/components/button/Button';
import ProfilePicture from 'src/components/profile/profilePicture/ProfilePicture';
import InputField from 'src/components/common/formFields/inputField/InputField';
import { componentSizeVariantsEnum } from 'src/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { TCreateCommentRequest, TPost } from 'src/services/apiEndpoint.types.ts';
import { TUserTypes } from 'src/constants/user.constants';

import './createComment.scss';
import createCommentValidationSchema from './createComment.validationSchema';
import { useNavigate } from 'react-router-dom';
import React, { useRef, useState } from 'react';
import { ConfirmModal } from 'src/components/common';
import { EmojiIcon } from 'src/components/common/common.icons';
import EmojiPicker from 'emoji-picker-react';
import { createComment } from 'src/store/venture/venture.slice';
import ExpandableInputField from 'src/components/common/expandableinput/ExpandableInputField';
import { socketService } from 'src/services/socket.api.service';

export type TCreateComment = {
  viewerId: string;
  viewerType: TUserTypes;
  ventureVerseId: string
};

export type TCreateCommentFormValues = {
  text: string;
};

// This component must know id and type of the viewer
const CreateVentureComment = ({
  ventureVerseId,
  viewerId,
  viewerType
}: TCreateComment) => {
  const initialValues: TCreateCommentFormValues = { text: '' };

  const dispatch = useAppDispatch();
  const navigate = useNavigate()

  const createCommentLoader: boolean = useAppSelector(
    (store) => store.venture.createCommentLoader
  );
  const { profile } = useAppSelector((store) => store.profile);
  const { myCompany, companyProfilePic } = useAppSelector((store) => store.company);
  const isAuthorized: boolean = useAppSelector<boolean>((store: any) => store.auth.isAuthorized);
  const postDescriptionRef = useRef<HTMLTextAreaElement>(null);
  const commentRef: any = useRef(null)
  const createCommentProfilePic = viewerType === 'User' ? profile?.profilePic : companyProfilePic;
  const createCommentName = viewerType === 'User' ? profile?.name : myCompany?.name;

  const [show, setShow] = useState<boolean>(false)
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);

  const {
    singleVenture
  } = useAppSelector(
    (store: any) => store.venture
  );

  const handleSubmit = async (
    { text }: TCreateCommentFormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    if (!isAuthorized) {
      setShow(true)
      return
    }
    // create comment api call
    const payload: TCreateCommentRequest = {
      [viewerType === 'Company' ? 'companyId' : "userId"]: viewerId,
      ventureVerseId,
      content: text
    };
    dispatch(createComment(payload)).then(() => {
      if (commentRef.current) {
        commentRef.current.style.height = '42px';
      }
      const performAction = (userId, userType) => {
        console.log("userId venture", userId);
        const userIds = [userId];
        if (viewerId !== singleVenture?.User.id) {
          socketService.emit("notifications-unread", {
            userIds,
            userType: userType
          });
        }
      };
      performAction(singleVenture?.User.id, "User");
      resetForm();
    });
  };
  function handleNavigate() {
    navigate('/login?prev=' + encodeURIComponent(`/view-venture?id=${ventureVerseId}&viewAs=null`));
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={createCommentValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, values }) => {
          return (
            <Form noValidate className="create-comment-venture">
              <div className="create-comment-venture__header">
                {isAuthorized && <ProfilePicture
                  url={createCommentProfilePic || ''}
                  fallback={createCommentName[0] || ''}
                />}
                {/* <InputField
                  placeholder="Comment"
                  id="text"
                  variant={componentSizeVariantsEnum.SMALL}
                  onChange={(e) => {
                    if (!isAuthorized) {
                      setShow(true)
                      return
                    }
                    setFieldValue('text', e.target.value);
                  }
                  }
                  value={values.text}
                  errorMessage={errors.text}
                  isTouched={touched.text}
                /> */}
                <ExpandableInputField
                  inputRef={commentRef}
                  id="text"
                  value={values.text}
                  onChange={(e) => {
                    if (!isAuthorized) {
                      setShow(true)
                      return
                    }
                    setFieldValue('text', e.target.value);
                  }}
                  placeholder="Comment"
                  isTouched={touched.text}
                  errorMessage={errors.text}
                />
              </div>
              <div className="create-comment-venture__emojiBtn__desktop">
                <EmojiIcon onClick={() => {
                  if (isAuthorized) {
                    setIsEmojiPickerOpen((prev) => !prev)
                  } else {
                    setShow(true)
                  }
                }} />
                {touched.text && errors.text && <div className='create-comment-venture__fake-error'>ff</div>}
              </div>

              <div className="create-comment-venture__buttons">
                {/* <Button variant="secondary" size="small" onClick={onClose}>
                Cancel
              </Button> */}
                <div>
                  {isAuthorized && <Button variant="primary" size="small" type="submit" loading={createCommentLoader}>
                    Comment
                  </Button>}
                  {touched.text && errors.text && <div className='create-comment-venture__fake-error'>ff</div>}
                </div>
              </div>
              <div className='commentEmojiPicker'>
                <EmojiPicker
                  open={isEmojiPickerOpen}
                  className='comment-emoji-picker'
                  onEmojiClick={(e) => {
                    if (isAuthorized) {
                      const postDescriptionEle = postDescriptionRef?.current;
                      if (postDescriptionEle) {
                        const startPos = postDescriptionEle?.selectionStart;

                        const textBeforeCursor = values?.text.substring(0, startPos);
                        const textAfterCursor = values?.text.substring(startPos);

                        setFieldValue('text', textBeforeCursor + e.emoji + textAfterCursor);
                      } else {
                        setFieldValue('text', values.text + e.emoji);
                      }
                    } else {
                      setShow(true)
                    }
                    setIsEmojiPickerOpen(false);
                  }}
                />
              </div>
            </Form>
          );
        }}
      </Formik>

      <ConfirmModal
        title=""
        description={<p style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', margin: '0' }}>
          <span>To enjoy all the features of <span style={{ color: '#fa4616' }}>htraction</span> </span>
          <span><span style={{ color: '#fa4616' }}>Sign in</span> to Continue</span>
        </p>}
        openConfirmModal={show}
        onClose={() => setShow(false)}
        onSubmit={handleNavigate}
        submitText="Sign in"
        type="confirm"
      />
    </>
  );
};

export default CreateVentureComment;
